import React, { useEffect, useState } from 'react';
import {
  buildColumnLabels,
  getDefaultColumnIndices,
} from '../modals/BulkCreateProductsModal/bulkCreateExcelUtils';
import { emptyMobileCreateItem } from './MobileBulkCreateItem';
import { readNumber } from './mobileProductUtils';
import MobileProductField from './MobileProductField';
import MobileProductFeedback from './MobileProductFeedback';
import useMobileMutation from './useMobileMutation';

const CURRENCIES = {
  uah: null,
  pounds: ['priceInPounds', 'pound_rate'],
  usd: ['priceInUSD', 'dollar_rate'],
  euro: ['priceInEuro', 'euro_rate'],
};
export const buildMobileImportItems = (rows, mapping, skipRows, currency, rates) => {
  if (mapping.nameColIdx === '' || mapping.articleColIdx === '')
    throw new Error('Оберіть колонки назви та артикулу');
  const foreign = CURRENCIES[currency];
  if (foreign && !(readNumber(rates[foreign[1]]) > 0))
    throw new Error('Для конвертації в гривні спочатку збережіть курс валюти');
  const cell = (row, key) =>
    mapping[key] === '' ? '' : String(row[Number(mapping[key])] ?? '').trim();
  return rows
    .slice(Number(skipRows))
    .filter(row => cell(row, 'nameColIdx') || cell(row, 'articleColIdx'))
    .map(row => {
      const amount = cell(row, 'priceColIdx')
        .replace(/\s/g, '')
        .replace(/[£$€₴]/g, '');
      const price =
        mapping.priceColIdx === ''
          ? '0'
          : foreign && Number.isFinite(readNumber(amount))
          ? String(Math.ceil(readNumber(amount) * readNumber(rates[foreign[1]])))
          : amount;
      return {
        ...emptyMobileCreateItem(),
        name: cell(row, 'nameColIdx'),
        productCode: cell(row, 'articleColIdx'),
        barcode: cell(row, 'barcodeColIdx'),
        quantity: mapping.quantityColIdx === '' ? '0' : cell(row, 'quantityColIdx'),
        price,
        ...(foreign
          ? { [foreign[0]]: Number.isFinite(readNumber(amount)) ? readNumber(amount) : null }
          : {}),
      };
    });
};

export default function MobileProductImport({ rates, onItems, disabled, onBusyChange }) {
  const [workbook, setWorkbook] = useState(null);
  const [sheet, setSheet] = useState('');
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [skipRows, setSkipRows] = useState('2');
  const [currency, setCurrency] = useState('pounds');
  const [name, setName] = useState('');
  const [xlsx, setXlsx] = useState(null);
  const mutation = useMobileMutation();
  useEffect(() => {
    onBusyChange(mutation.busy);
  }, [mutation.busy, onBusyChange]);
  const applySheet = (api, book, selected) => {
    const data = api.utils.sheet_to_json(book.Sheets[selected], {
      header: 1,
      defval: '',
      raw: false,
      blankrows: false,
    });
    const columns = data.reduce((max, row) => Math.max(max, row.length), 0);
    setRows(data);
    setMapping(getDefaultColumnIndices(columns));
    setSheet(selected);
  };
  const readFile = file =>
    mutation.run(
      async () => {
        const module = await import('xlsx');
        const api = module.default || module;
        const buffer = await file.arrayBuffer();
        return { api, book: api.read(buffer, { type: 'array', cellDates: true }) };
      },
      ({ api, book }) => {
        if (!book.SheetNames.length) {
          mutation.setError('Файл не містить аркушів');
          return;
        }
        setXlsx(api);
        setWorkbook(book);
        setName(file.name);
        applySheet(api, book, book.SheetNames[0]);
      },
    );
  const columns = buildColumnLabels(rows.reduce((max, row) => Math.max(max, row.length), 0));
  return (
    <fieldset className='mp-import' disabled={disabled || mutation.busy}>
      <legend>Імпорт Excel</legend>
      <MobileProductFeedback error={mutation.error} loading={mutation.busy} />
      <label className='mp-field'>
        <span>Файл .xlsx або .xls</span>
        <input
          type='file'
          accept='.xlsx,.xls'
          onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) readFile(file);
          }}
        />
      </label>
      {workbook && (
        <>
          <p className='mp-note'>
            {name} · {rows.length} рядків
          </p>
          <MobileProductField label='Аркуш'>
            <select
              className='form-control'
              value={sheet}
              onChange={event => applySheet(xlsx, workbook, event.target.value)}
            >
              {workbook.SheetNames.map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </MobileProductField>
          <MobileProductField
            label='Пропустити перші рядки'
            type='number'
            min='0'
            inputMode='numeric'
            value={skipRows}
            onChange={setSkipRows}
          />
          <MobileProductField label='Валюта ціни у файлі'>
            <select
              className='form-control'
              value={currency}
              onChange={event => setCurrency(event.target.value)}
            >
              <option value='pounds'>Фунт £</option>
              <option value='usd'>Долар $</option>
              <option value='euro'>Євро €</option>
              <option value='uah'>Гривня</option>
            </select>
          </MobileProductField>
          {[
            ['nameColIdx', 'Назва'],
            ['articleColIdx', 'Артикул'],
            ['barcodeColIdx', 'Штрихкод'],
            ['priceColIdx', 'Ціна'],
            ['quantityColIdx', 'Кількість'],
          ].map(([key, label]) => (
            <MobileProductField key={key} label={`Колонка: ${label}`}>
              <select
                className='form-control'
                value={mapping[key]}
                onChange={event => setMapping(prev => ({ ...prev, [key]: event.target.value }))}
              >
                <option value=''>Не вибрана</option>
                {columns.map((column, index) => (
                  <option key={column} value={index}>
                    {column}: {String(rows[Number(skipRows)]?.[index] || '').slice(0, 60)}
                  </option>
                ))}
              </select>
            </MobileProductField>
          ))}
          <p className='mp-note'>
            Імпорт замінить поточний список для створення. Перед відправленням ви зможете перевірити
            та змінити товари.
          </p>
          <button
            type='button'
            className='btn btn-dark'
            onClick={() => {
              try {
                if (!/^\d+$/.test(skipRows))
                  throw new Error('Кількість пропущених рядків має бути цілим невід’ємним числом');
                const items = buildMobileImportItems(rows, mapping, skipRows, currency, rates);
                if (!items.length) throw new Error('Немає рядків для імпорту');
                onItems(items);
                mutation.setError('');
              } catch (error) {
                mutation.setError(error.message);
              }
            }}
          >
            Сформувати список
          </button>
        </>
      )}
    </fieldset>
  );
}
